import { ReactElement, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { tbColors } from 'styles/colors';
import type { ChartTooltipPopperFrameHandle } from 'components/chart/ChartTooltipPopperFrame';
import { ChartTooltipPopperFrame } from 'components/chart/ChartTooltipPopperFrame';
import { ChartTooltipCard } from 'components/chart/ChartTooltipCard';
import { MeasureComparison } from 'components/MeasureComparison';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import type { Measure } from 'services/model/model.service';
import { useModelService } from 'services/model/model.service';
import type { DataResult } from 'models/data';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { useAppStore } from 'hooks/state/useAppStore';
import { Tile } from 'components/Layout/PageContainer';

const COLOR_ANCHOR = tbColors.all.darkBlue[500];
const COLOR_DEDUCTION = tbColors.all.coral[300];
const COLOR_RESULT_POSITIVE = tbColors.all.turquoise[500];
const COLOR_RESULT_NEGATIVE = tbColors.all.coral[500];

const CHART_H = 280;
const LABEL_TOP_H = 24;
const LABEL_BOTTOM_H = 32;
const DRAWABLE_H = CHART_H - LABEL_TOP_H - LABEL_BOTTOM_H;

type BarType = 'anchor' | 'deduction' | 'result';

interface WaterfallColumn {
  pct: number;
  displayPct?: number;
  comparePct: number;
  measure: Measure;
  floatFromPct: number;
  type: BarType;
  valOnly?: boolean;
}

function scale(pct: number, drawableH: number): number {
  return Math.abs(pct) * drawableH;
}

function barColor(type: BarType, pct: number): string {
  if (type === 'anchor') return COLOR_ANCHOR;
  if (type === 'deduction') return COLOR_DEDUCTION;
  return pct >= 0 ? COLOR_RESULT_POSITIVE : COLOR_RESULT_NEGATIVE;
}

interface TooltipState {
  col: WaterfallColumn;
  data: DataResult;
}

interface Props {
  data: DataResult;
  comparisonPeriod?: string;
}

export function ProfitabilityWaterfallChart({ data, comparisonPeriod = undefined }: Props) {
  const { values, compareValues = {} } = data;
  const intl = useIntl();
  const { getMeasures } = useModelService();
  const popperRef = useRef<ChartTooltipPopperFrameHandle | null>(null);
  const [tooltipState, setTooltipState] = useState<TooltipState | null>(null);

  const gmvPct = 1;

  const [
    netSlsGmvVal,
    netSlsCogVal,
    netSlsCogRate,
    cm1Val,
    cm1Rate,
    netSlsFeeVal,
    netSlsFeeRate,
    netSlsLogCostVal,
    netSlsLogCostRate,
    cm2Val,
    cm2Rate,
  ] = getMeasures([
    'netSlsGmvValExtExpOD',
    'netSlsCogValExtExpOD',
    'netSlsCogRateExtExpOD',
    'cm1ValExtExpOD',
    'cm1RateExtExpOD',
    'netSlsFeeValExtExpOD',
    'netSlsFeeRateExtExpOD',
    'netSlsLogCostValExtExpOD',
    'netSlsLogCostRateExtExpOD',
    'cm2ValExtExpOD',
    'cm2RateExtExpOD',
  ]);

  const labelContext = useDatePickerLabels();

  const { dateRange } = useAppStore();

  const comparison = dateRange.comparison?.from && dateRange.comparison.to;

  const comparisonPeriodId = comparisonPeriod || labelContext.comparisonRangeLabel;
  const comparisonPeriodLabel = comparisonPeriodId ? intl.formatMessage({ id: comparisonPeriodId }) : '';
  const netSlsCogRateOD = netSlsCogRate.getValue(values);
  const netSlsFeeRateOD = netSlsFeeRate.getValue(values);
  const cm2RateOD = cm2Rate.getValue(values);

  const cm1RateOD = cm1Rate.getValue(values);
  const logCstRateOD = netSlsLogCostRate.getValue(values);

  const feesBottomPct = cm1RateOD - netSlsFeeRateOD;
  const logisticsBottomPct = feesBottomPct - logCstRateOD;

  const feesRounded = Math.round(netSlsFeeRateOD * 10000) / 10000;
  const feesAndCostsRounded = Math.round((netSlsFeeRateOD + logCstRateOD) * 10000) / 10000;
  const logisticsDisplayPct = feesAndCostsRounded - feesRounded;

  const minPct = Math.min(0, netSlsCogRateOD, cm1RateOD, netSlsFeeRateOD, logisticsDisplayPct, cm2RateOD);
  const maxPct = Math.max(gmvPct, netSlsCogRateOD, cm1RateOD, netSlsFeeRateOD, logisticsDisplayPct, cm2RateOD);

  const totalRange = maxPct - minPct || 1;
  const zeroY = LABEL_TOP_H + DRAWABLE_H * (maxPct / totalRange);

  const columns: WaterfallColumn[] = [
    {
      pct: 1,
      comparePct: 1,
      measure: netSlsGmvVal,
      floatFromPct: 0,
      type: 'anchor',
      valOnly: true,
    },
    {
      pct: netSlsCogRateOD,
      comparePct: netSlsCogRate.getValue(compareValues),
      measure: netSlsCogVal,
      floatFromPct: 1,
      type: 'deduction',
    },
    {
      pct: cm1RateOD,
      comparePct: cm1Rate.getValue(compareValues),
      measure: cm1Val,
      floatFromPct: 0,
      type: 'anchor',
    },
    {
      pct: netSlsFeeRateOD,
      comparePct: netSlsFeeRate.getValue(compareValues),
      measure: netSlsFeeVal,
      floatFromPct: cm1RateOD,
      type: 'deduction',
    },
    {
      pct: logCstRateOD,
      comparePct: netSlsLogCostRate.getValue(compareValues),
      measure: netSlsLogCostVal,
      floatFromPct: feesBottomPct,
      type: 'deduction',
      displayPct: logisticsDisplayPct,
    },
    {
      pct: cm2RateOD,
      comparePct: cm2Rate.getValue(compareValues),
      measure: cm2Val,
      floatFromPct: logisticsBottomPct,
      type: 'result',
    },
  ];

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, col: WaterfallColumn) => {
      setTooltipState({ col, data });
      popperRef.current?.openAt(e.currentTarget);
    },
    [data]
  );

  const handleMouseLeave = useCallback(() => {
    popperRef.current?.close();
    setTooltipState(null);
  }, []);

  function renderRow(isComparison: boolean, col: WaterfallColumn, absValue: number, pctValue: number) {
    const key = isComparison ? 'comparison' : 'current';

    const rowValues: { key: string; content: ReactElement; minWidth?: number }[] = col.valOnly
      ? []
      : [
          {
            key: `${key}-share`,
            content: (
              <MeasureFormat
                value={pctValue}
                unit={MeasureUnitCode.PERCENT}
                config={isComparison ? { isDiff: true, hasSign: true, decimalPlaces: 2 } : { decimalPlaces: 2 }}
              />
            ),
            minWidth: 72,
          },
        ];

    rowValues.push({
      key: `${key}-value`,
      content: <MeasureFormat value={absValue} unit={MeasureUnitCode.CURRENCY} />,
    });

    return {
      key: key,
      label: isComparison ? comparisonPeriodLabel : col.measure.metric.name,
      values: rowValues,
    };
  }

  const renderTooltip = () => {
    if (!tooltipState) return <span />;

    const { col, data: d } = tooltipState;
    const pctValue = col.displayPct ?? col.pct;
    const comparePctValue = col.comparePct;
    const absValue = col.measure.getValue(d.values);
    const compareAbsValue = col.measure.getValue(d.compareValues);
    const deltaPp = (pctValue - comparePctValue) * 100;

    const rows = [renderRow(false, col, absValue, pctValue)];
    if (comparison) {
      rows.push(renderRow(true, col, compareAbsValue, deltaPp / 100));
    }

    return (
      <ChartTooltipCard
        title={col.measure.metric.name}
        headerAccessory={
          col.valOnly || !comparison ? undefined : (
            <MeasureComparison
              value={pctValue}
              comparisonValue={comparePctValue}
              unit={MeasureUnitCode.PERCENT}
              interpretation={col.measure.metric.interpretation}
            />
          )
        }
        rows={rows}
      />
    );
  };

  return (
    <Tile titleId="profitability.waterfall.title" data-waterfall-root>
      <b className="m-2" />
      <ChartTooltipPopperFrame ref={popperRef} placement="top">
        {renderTooltip()}
      </ChartTooltipPopperFrame>

      <div style={{ position: 'relative', height: CHART_H }}>
        <div className="absolute inset-x-0 h-px bg-snow-500" style={{ top: zeroY }} />

        <div className="flex h-full items-stretch">
          {(() => {
            const barLayouts = columns.map(col => {
              const isNegative = col.pct < 0;
              const barH = scale(col.pct, DRAWABLE_H / totalRange);
              const floatY = zeroY - scale(col.floatFromPct, DRAWABLE_H / totalRange);
              let barTop: number;
              if (col.type === 'anchor') {
                barTop = zeroY - barH;
              } else if (isNegative) {
                barTop = zeroY;
              } else {
                barTop = floatY;
              }
              return { col, barH, barTop };
            });
            const axisLabelY = Math.max(...barLayouts.map(({ barTop, barH }) => barTop + barH)) + 8;
            return barLayouts.map(({ col, barH, barTop }) => (
              <div key={col.measure.code} className="flex-1 relative">
                <div
                  className="absolute inset-x-0 text-center text-xs whitespace-nowrap font-bold"
                  style={{ top: 0, color: tbColors.all.ash[500] }}
                >
                  <MeasureFormat
                    value={col.displayPct ?? col.pct}
                    unit={MeasureUnitCode.PERCENT}
                    config={{ decimalPlaces: 2 }}
                  />
                </div>
                {barH > 0 && (
                  <div
                    className="absolute left-[15%] right-[15%] rounded-sm cursor-pointer"
                    style={{
                      top: barTop,
                      height: barH,
                      backgroundColor: barColor(col.type, col.pct),
                    }}
                    onMouseEnter={e => handleMouseEnter(e, col)}
                    onMouseLeave={handleMouseLeave}
                  />
                )}
                <div className="absolute inset-x-0 text-center text-xs text-ash-500" style={{ top: axisLabelY }}>
                  {col.measure.metric.name}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
    </Tile>
  );
}
